import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { breakpoints, colorCSS } from '../styles';
export function Carousel({ arrows, dots, children, onChange, selectedIndex, initialAutoplay = false }) {
    //current slide #
    const [slide, setSlide] = useState(selectedIndex ? selectedIndex : 0);
    //current track offset
    const [marginLeft, setMarginLeft] = useState(0);
    //drag information (start of drag, margin at start)
    const [drag, setDrag] = useState(null);
    //current frame size
    const [frameWidth, setFrameWidth] = useState(0);
    const [preventScroll, setPreventScroll] = useState(false);
    const [autoplay, setAutoplay] = useState(initialAutoplay);
    //frame ref
    const frame = useRef(null);
    //enforce array-ness of children
    const slides = !children
        ? []
        : !Array.isArray(children)
            ? [children]
            : children;
    const hasMultipleSlides = slides.length > 1;
    //maintain current width of the frame
    useEffect(() => {
        const handleResize = () => {
            var _a, _b;
            setFrameWidth((_b = (_a = frame.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [frame]);
    //prevent vertical scrolling when sliding carousel
    useEffect(() => {
        if (preventScroll) {
            document.body.classList.add('prevent-scroll');
            document.ontouchmove = (e) => {
                e.preventDefault();
            };
        }
        else {
            document.body.classList.remove('prevent-scroll');
            document.ontouchmove = () => true;
        }
        return () => {
            document.body.classList.remove('prevent-scroll');
            document.ontouchmove = () => true;
        };
    }, [preventScroll]);
    //listen to slide updates
    useEffect(() => {
        if (onChange)
            onChange(slide);
    }, [slide]);
    //change slide from parent element
    useEffect(() => {
        if (selectedIndex !== undefined && selectedIndex !== slide) {
            //stop autoplay if it's running
            if (autoplay)
                setAutoplay(false);
            setSlide(selectedIndex);
        }
    }, [selectedIndex]);
    //handle autoplay
    useEffect(() => {
        const timeout = autoplay && initialAutoplay
            ? setTimeout(() => {
                nextSlide();
            }, 5000)
            : null;
        return () => {
            if (timeout)
                clearTimeout(timeout);
        };
    }, [autoplay, initialAutoplay, slide]);
    //go to the current slide
    const goToSlide = () => {
        setMarginLeft(0 - slide * frameWidth);
    };
    //set margin when window resizes or slide changes
    useEffect(goToSlide, [slide, frameWidth]);
    //go to previous slide
    const prevSlide = (e) => {
        if (!hasMultipleSlides)
            return;
        if (e) {
            //arrow was clicked; stop autoplay if it's running
            if (autoplay)
                setAutoplay(false);
        }
        setSlide((slide ? slide : slides.length) - 1);
    };
    //go to next slide
    const nextSlide = (e) => {
        if (!hasMultipleSlides)
            return;
        if (e) {
            //arrow was clicked; stop autoplay if it's running
            if (autoplay)
                setAutoplay(false);
        }
        setSlide(slide + 1 === slides.length ? 0 : slide + 1);
    };
    const dragStart = (x, y) => {
        setDrag({ dragStart: [x, y], startingMargin: marginLeft });
    };
    const dragging = (x, y) => {
        //check if drag started on carousel
        if (drag === null)
            return;
        //stop autoplay if it's running
        if (autoplay)
            setAutoplay(false);
        //exit if dragging vertically
        const horizontalOffset = x - drag.dragStart[0];
        const verticalOffset = y - drag.dragStart[1];
        if (Math.abs(horizontalOffset) < Math.abs(verticalOffset))
            return;
        //horizontal touch, lock vertical scroll
        if (!preventScroll)
            setPreventScroll(true);
        //move track
        const dragAmount = horizontalOffset + drag.startingMargin;
        const maxAmount = 0;
        const minAmount = 0 - frameWidth * (slides.length - 1);
        const friction = 3;
        const amountWithFriction = dragAmount > maxAmount
            ? dragAmount / friction
            : dragAmount < minAmount
                ? minAmount + (dragAmount - minAmount) / friction
                : dragAmount;
        setMarginLeft(amountWithFriction);
    };
    const dragEnd = (x) => {
        if (drag !== null) {
            const dragAmount = drag.dragStart[0] - x;
            const dragDistance = frameWidth / 3; //more than one third
            setDrag(null);
            setPreventScroll(false);
            if (dragAmount > dragDistance && slide !== slides.length - 1) {
                //go to next item
                nextSlide();
            }
            else if (dragAmount < 0 - dragDistance && slide) {
                //go to previous item
                prevSlide();
            }
            else {
                //restore current item
                goToSlide();
            }
        }
    };
    return (React.createElement(Frame, { "data-testid": "carousel", style: {
            visibility: frameWidth ? 'visible' : 'hidden',
            paddingBottom: dots && hasMultipleSlides ? '2rem' : undefined
        }, ref: frame },
        React.createElement(Mask, null,
            React.createElement(Track, { style: {
                    cursor: drag ? 'grabbing' : 'grab',
                    marginLeft: marginLeft,
                    transition: drag ? 'none' : 'margin-left 0.1s ease',
                    width: frameWidth * slides.length
                }, onTouchStart: (e) => {
                    dragStart(e.touches[0].clientX, e.touches[0].clientY);
                }, onTouchMove: (e) => {
                    dragging(e.touches[0].clientX, e.touches[0].clientY);
                }, onTouchEnd: (e) => {
                    dragEnd(e.changedTouches[0].clientX);
                }, onMouseDown: (e) => {
                    dragStart(e.clientX, e.clientY);
                }, onMouseMove: (e) => {
                    dragging(e.clientX, e.clientY);
                }, onMouseUp: (e) => {
                    dragEnd(e.clientX);
                }, onMouseOut: (e) => {
                    dragEnd(e.clientX);
                } }, slides.map((child, index) => (React.createElement(Slide, { key: index }, child))))),
        arrows && hasMultipleSlides && (React.createElement(React.Fragment, null,
            React.createElement(ArrowPrev, { onClick: prevSlide, dots: dots && hasMultipleSlides },
                React.createElement("svg", { width: "21", height: "36", viewBox: "0 0 21 36" },
                    React.createElement("path", { d: "M5.22238 18.0599L19.3476 32.0019C20.0193 32.6649 20.0193 33.7398 19.3476 34.4028C18.6759 35.0658 17.5869 35.0658 16.9152 34.4028L1.71239 19.3973C1.34119 19.0309 1.17513 18.5387 1.2142 18.0597C1.17524 17.5809 1.34132 17.0889 1.71242 16.7226L16.9152 1.71697C17.5869 1.05398 18.676 1.05398 19.3477 1.71697C20.0194 2.37996 20.0194 3.45488 19.3477 4.11787L5.22238 18.0599Z" }),
                    React.createElement("path", { d: "M5.22238 18.0599L4.5199 17.3482L3.79884 18.0599L4.5199 18.7717L5.22238 18.0599ZM19.3476 32.0019L18.6452 32.7137L19.3476 32.0019ZM19.3476 34.4028L20.0501 35.1145L20.0501 35.1145L19.3476 34.4028ZM16.9152 34.4028L16.2127 35.1145L16.9152 34.4028ZM1.71239 19.3973L2.41487 18.6856L2.41487 18.6856L1.71239 19.3973ZM1.2142 18.0597L2.21089 18.1411L2.21751 18.0599L2.21091 17.9786L1.2142 18.0597ZM1.71242 16.7226L1.00995 16.0109L1.00995 16.0109L1.71242 16.7226ZM16.9152 1.71697L16.2127 1.00526V1.00526L16.9152 1.71697ZM19.3477 1.71697L20.0501 1.00526L19.3477 1.71697ZM19.3477 4.11787L20.0501 4.82957L19.3477 4.11787ZM4.5199 18.7717L18.6452 32.7137L20.0501 31.2902L5.92485 17.3482L4.5199 18.7717ZM18.6452 32.7137C18.9202 32.9851 18.9202 33.4197 18.6452 33.6911L20.0501 35.1145C21.1185 34.06 21.1185 32.3448 20.0501 31.2902L18.6452 32.7137ZM18.6452 33.6911C18.3629 33.9697 17.8999 33.9697 17.6177 33.6911L16.2127 35.1145C17.2739 36.1619 18.989 36.1619 20.0501 35.1145L18.6452 33.6911ZM17.6177 33.6911L2.41487 18.6856L1.00992 20.109L16.2127 35.1145L17.6177 33.6911ZM2.41487 18.6856C2.2615 18.5342 2.19502 18.3355 2.21089 18.1411L0.217512 17.9784C0.155228 18.7419 0.420878 19.5276 1.00992 20.109L2.41487 18.6856ZM2.21091 17.9786C2.19509 17.7842 2.26157 17.5856 2.4149 17.4343L1.00995 16.0109C0.42106 16.5921 0.155394 17.3776 0.217494 18.1408L2.21091 17.9786ZM2.4149 17.4343L17.6177 2.42868L16.2127 1.00526L1.00995 16.0109L2.4149 17.4343ZM17.6177 2.42868C17.9 2.15008 18.3629 2.15008 18.6452 2.42868L20.0501 1.00526C18.989 -0.0421169 17.2739 -0.0421197 16.2127 1.00526L17.6177 2.42868ZM18.6452 2.42868C18.9202 2.70011 18.9202 3.13472 18.6452 3.40616L20.0501 4.82957C21.1185 3.77503 21.1185 2.0598 20.0501 1.00526L18.6452 2.42868ZM18.6452 3.40616L4.5199 17.3482L5.92486 18.7717L20.0501 4.82957L18.6452 3.40616Z" }))),
            React.createElement(ArrowNext, { onClick: nextSlide, dots: dots && hasMultipleSlides },
                React.createElement("svg", { width: "21", height: "36", viewBox: "0 0 21 36" },
                    React.createElement("path", { d: "M15.7776 18.0599L1.65242 32.0019C0.980724 32.6649 0.980722 33.7398 1.65242 34.4028C2.32412 35.0658 3.41316 35.0658 4.08486 34.4028L19.2876 19.3973C19.6588 19.0309 19.8248 18.5388 19.7858 18.0599C19.8248 17.581 19.6588 17.0889 19.2876 16.7226L4.08476 1.71697C3.41306 1.05398 2.32401 1.05398 1.65231 1.71697C0.980602 2.37996 0.980602 3.45488 1.65231 4.11787L15.7776 18.0599Z" }),
                    React.createElement("path", { d: "M15.7776 18.0599L16.4801 17.3482L17.2012 18.0599L16.4801 18.7717L15.7776 18.0599ZM1.65242 32.0019L0.949947 31.2902L1.65242 32.0019ZM1.65242 34.4028L2.3549 33.6911H2.3549L1.65242 34.4028ZM4.08486 34.4028L3.38238 33.6911H3.38239L4.08486 34.4028ZM19.2876 19.3973L19.9901 20.109L19.9901 20.109L19.2876 19.3973ZM19.7858 18.0599L18.7891 18.1411L18.7825 18.0599L18.7891 17.9787L19.7858 18.0599ZM19.2876 16.7226L18.5851 17.4343H18.5851L19.2876 16.7226ZM4.08476 1.71697L4.78724 1.00526L4.78724 1.00526L4.08476 1.71697ZM1.65231 1.71697L0.949829 1.00526L0.949831 1.00526L1.65231 1.71697ZM1.65231 4.11787L0.949831 4.82957L0.949829 4.82957L1.65231 4.11787ZM16.4801 18.7717L2.3549 32.7137L0.949947 31.2902L15.0752 17.3482L16.4801 18.7717ZM2.3549 32.7136C2.0799 32.9851 2.0799 33.4197 2.3549 33.6911L0.949945 35.1145C-0.118454 34.06 -0.118448 32.3448 0.949947 31.2902L2.3549 32.7136ZM2.3549 33.6911C2.63716 33.9697 3.10013 33.9697 3.38238 33.6911L4.78734 35.1145C3.7262 36.1619 2.01109 36.1619 0.949944 35.1145L2.3549 33.6911ZM3.38239 33.6911L18.5851 18.6856L19.9901 20.109L4.78734 35.1145L3.38239 33.6911ZM18.5851 18.6856C18.7385 18.5342 18.805 18.3356 18.7891 18.1411L20.7825 17.9787C20.8447 18.742 20.5791 19.5276 19.9901 20.109L18.5851 18.6856ZM18.7891 17.9787C18.805 17.7843 18.7385 17.5856 18.5851 17.4343L19.9901 16.0109C20.579 16.5922 20.8447 17.3778 20.7825 18.1411L18.7891 17.9787ZM18.5851 17.4343L3.38229 2.42868L4.78724 1.00526L19.9901 16.0109L18.5851 17.4343ZM3.38229 2.42868C3.10002 2.15008 2.63704 2.15008 2.35478 2.42868L0.949831 1.00526C2.01098 -0.04212 3.72609 -0.0421157 4.78724 1.00526L3.38229 2.42868ZM2.35478 2.42868C2.07978 2.70011 2.07978 3.13472 2.35478 3.40616L0.949829 4.82957C-0.118572 3.77503 -0.118572 2.0598 0.949829 1.00526L2.35478 2.42868ZM2.35478 3.40616L16.4801 17.3482L15.0752 18.7717L0.949831 4.82957L2.35478 3.40616Z" }))))),
        dots && hasMultipleSlides && (React.createElement(Dots, null, slides.map((child, index) => !!child && (React.createElement(Dot, { key: index, "data-selected": index === slide, onClick: () => {
                //stop autoplay if it's running
                if (autoplay)
                    setAutoplay(false);
                setSlide(index);
            } })))))));
}
const Frame = styled.div `
  position: relative;
`;
const Mask = styled.div `
  overflow: hidden;
`;
const Track = styled.div `
  align-items: stretch;
  display: flex;
`;
const Slide = styled.div `
  box-sizing: content-box;
  padding: 1rem;
  pointer-events: none;
  user-select: none;
  width: 100%;
  a {
    pointer-events: auto;
  }
`;
const Arrow = styled.button `
  align-items: center;
  background-color: transparent;
  border: 0;
  cursor: pointer;
  display: none;
  height: 5rem;
  justify-content: center;
  margin-top: ${({ dots }) => (dots ? '-3.5rem' : '-2.5rem')};
  outline: none !important;
  position: absolute;
  top: 50%;
  width: 5rem;
  z-index: 2;
  svg path {
    fill: ${colorCSS('N50')};
  }
  &:hover svg path {
    fill: ${colorCSS('T300')} !important;
  }
  &:focus svg path {
    fill: ${colorCSS('N100')};
  }
  @media (min-width: ${breakpoints.md}px) {
    display: flex;
  }
`;
const ArrowPrev = styled(Arrow) `
  left: -4rem;
  @media (min-width: ${breakpoints.xl}px) {
    left: -5rem;
  }
`;
const ArrowNext = styled(Arrow) `
  right: -4rem;
  @media (min-width: ${breakpoints.xl}px) {
    right: -5rem;
  }
`;
const Dots = styled.div `
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  position: absolute;
  width: 100%;
`;
const Dot = styled.button `
  background-color: ${colorCSS('N50')};
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  display: block;
  height: 0.75rem;
  margin: 0 0.5rem;
  padding: 0;
  width: 0.75rem;
  &:hover,
  &:focus {
    background-color: ${colorCSS('T300')};
    outline: none;
  }
  &[data-selected='true'] {
    background-color: ${colorCSS('T300')};
    height: 1rem;
    width: 1rem;
  }
`;
//# sourceMappingURL=Carousel.js.map